/**
 * Query keys, in one place so invalidation after a mutation can never miss a reader.
 * Hierarchical: invalidating `qk.strategies.all` refreshes every strategy query.
 */
export const qk = {
  me: ['me'] as const,
  balance: (q?: Record<string, unknown>) => ['testing', 'balance', q ?? {}] as const,
  usage: ['testing', 'usage'] as const,
  ranking: ['ranking'] as const,
  instruments: ['catalogue', 'instruments'] as const,
  indicators: ['catalogue', 'indicators'] as const,
  indicatorFavourites: ['me', 'indicator-favourites'] as const,
  weightPresets: ['catalogue', 'weight-presets'] as const,
  candles: (instrumentId: string, timeframe: string, limit: number, endAt?: string) =>
    ['candles', instrumentId, timeframe, limit, endAt ?? null] as const,
  strategies: {
    all: ['strategies'] as const,
    list: ['strategies', 'list'] as const,
    detail: (id: string) => ['strategies', 'detail', id] as const,
    conditions: (id: string, instrumentId: string) => ['strategies', 'conditions', id, instrumentId] as const,
    series: (id: string, ruleId: string, instrumentId: string, timeframe: string, endAt?: string) =>
      ['strategies', 'series', id, ruleId, instrumentId, timeframe, endAt ?? null] as const,
    matchFrequency: (id: string) => ['strategies', 'match-frequency', id] as const,
  },
  applications: ['applications'] as const,
  templates: ['templates'] as const,
  watchlists: {
    all: ['watchlists'] as const,
    list: ['watchlists', 'list'] as const,
    detail: (id: string) => ['watchlists', 'detail', id] as const,
  },
  alerts: {
    all: ['alerts'] as const,
    list: (status?: string, unread?: boolean) => ['alerts', 'list', status ?? 'all', unread ?? false] as const,
    detail: (id: string) => ['alerts', 'detail', id] as const,
    series: (id: string, ruleId: string, timeframe: string) => ['alerts', 'series', id, ruleId, timeframe] as const,
  },
  backtests: {
    all: ['backtests'] as const,
    list: (q?: Record<string, unknown>) => ['backtests', 'list', q ?? {}] as const,
    detail: (id: string) => ['backtests', 'detail', id] as const,
    trades: (id: string) => ['backtests', 'trades', id] as const,
    analytics: (id: string) => ['backtests', 'analytics', id] as const,
    tradeChart: (id: string, index: number, timeframe?: string) =>
      ['backtests', 'trade-chart', id, index, timeframe ?? null] as const,
    suggestedCosts: (instrumentId: string) => ['backtests', 'suggested-costs', instrumentId] as const,
  },
  exitStrategies: ['exit-strategies'] as const,
  entryChecks: {
    all: ['entry-checks'] as const,
    list: (q?: Record<string, unknown>) => ['entry-checks', 'list', q ?? {}] as const,
    detail: (id: string) => ['entry-checks', 'detail', id] as const,
  },
  refine: {
    all: ['refine'] as const,
    options: ['refine', 'options'] as const,
    list: ['refine', 'list'] as const,
    detail: (id: string, limit?: number) => ['refine', 'detail', id, limit ?? null] as const,
    progress: (id: string) => ['refine', 'progress', id] as const,
    markings: ['refine', 'markings'] as const,
    entriesQuote: (spans: unknown) => ['refine', 'entries-quote', spans] as const,
    seedMatches: (strategyId: string, instrumentId: string) => ['refine', 'seed-matches', strategyId, instrumentId] as const,
  },
  parameterSearches: {
    all: ['parameter-searches'] as const,
    list: ['parameter-searches', 'list'] as const,
    detail: (id: string) => ['parameter-searches', 'detail', id] as const,
    options: (q: Record<string, unknown>) => ['parameter-searches', 'options', q] as const,
  },
  scanner: {
    snapshot: (timeframe: string) => ['scanner', 'snapshot', timeframe] as const,
    timeline: (strategyId: string, days: number) => ['scanner', 'timeline', strategyId, days] as const,
  },
  calendar: {
    events: (q: Record<string, unknown>) => ['calendar', 'events', q] as const,
    earnings: (q: Record<string, unknown>) => ['calendar', 'earnings', q] as const,
  },
  markets: {
    defaults: ['markets', 'defaults'] as const,
    performance: (ids: string[], start: string) => ['markets', 'performance', ids, start] as const,
    cone: (id: string) => ['markets', 'volatility-cone', id] as const,
    ranked: (id: string, timeframe: string, window: number) => ['markets', 'correlation-ranked', id, timeframe, window] as const,
    grid: (ids: string[], timeframe: string, window: number) => ['markets', 'correlation-grid', ids, timeframe, window] as const,
    regimes: (assetClass: string) => ['markets', 'regimes', assetClass] as const,
    regimeSeries: (id: string) => ['markets', 'regime-series', id] as const,
    pair: (id: string, pairId: string, timeframe: string, window: number) => ['markets', 'correlation-pair', id, pairId, timeframe, window] as const,
    profile: (id: string) => ['markets', 'asset-profile', id] as const,
    intraday: (id: string) => ['markets', 'intraday-profile', id] as const,
    trend: (assetClass: string) => ['markets', 'trend', assetClass] as const,
    trendSeries: (id: string) => ['markets', 'trend-series', id] as const,
    gaps: (id: string) => ['markets', 'gaps', id] as const,
    gapsVolume: (id: string) => ['markets', 'gaps-intraday-volume', id] as const,
    earnings: (id: string) => ['markets', 'earnings', id] as const,
    earningsIntraday: (id: string) => ['markets', 'earnings-intraday', id] as const,
    eventKinds: (id: string) => ['markets', 'event-kinds', id] as const,
    events: (id: string, kind: string, window: string) => ['markets', 'events', id, kind, window] as const,
    fundamentals: (id: string) => ['markets', 'fundamentals', id] as const,
    fundamentalsRanked: ['markets', 'fundamentals-ranked'] as const,
    insiders: (id: string) => ['markets', 'insiders', id] as const,
    insiderPurchases: ['markets', 'insider-purchases'] as const,
    strength: (q: Record<string, unknown>) => ['markets', 'relative-strength', q] as const,
  },
  legal: ['legal', 'current'] as const,
  plans: ['billing', 'plans'] as const,
  subscription: ['billing', 'subscription'] as const,
  referrals: ['referrals', 'me'] as const,
  preferences: ['preferences', 'web-layout'] as const,
};
